import React, { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  closeOnOverlayClick?: boolean;
  /** Overrides default indigo submit styling (e.g. `bg-bridge-accent`). */
  submitClassName?: string;
  submitDisabled?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  children,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  closeOnOverlayClick = true,
  submitClassName,
  submitDisabled = false,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    // z-[100] clears the app topbar (z-[60]); at a lower layer the header paints over the
    // dialog and swallows its heading.
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10">
      {/* overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity animate-pageIn motion-reduce:animate-none"
        onClick={() => closeOnOverlayClick && onClose()}
        aria-hidden
      />

      {/* Dialog panel. Bounded by the viewport and laid out as a column so the header and
          the footer stay put while only the body scrolls — without this a tall form pushes
          its own heading off the top of the screen, behind the app header. */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-lg transform transition-all animate-popIn motion-reduce:animate-none"
      >
          <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-bridge-border/60">
              {title && <div>
                  <h2 className="text-lg font-bold text-bridge-fg flex">{title}</h2>
                  {subtitle && <p className="text-xs text-bridge-muted mt-0.5">
                      {subtitle}
                  </p>}
              </div>}
              <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-white/5 text-bridge-muted hover:text-bridge-fg transition-colors"
              >
                  <X size={18} />
              </button>
          </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-6 scrollbar-metal">{children}</div>
        <div className="shrink-0 px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-bridge-muted hover:text-bridge-fg bg-transparent hover:bg-white/5 border border-bridge-border/70 rounded-xl transition-all">
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitDisabled}
            className={cn(
              "px-5 py-2 text-sm font-bold text-white rounded-xl shadow disabled:opacity-50 transition-all",
              submitClassName ?? "bg-indigo-600 hover:bg-indigo-700",
            )}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dialog;

